'use client';

import Image from 'next/image';
import { use } from 'react';
import { Card, Grid, HoverCard, Table, Tabs, Text } from '@mantine/core';
import {
  IconHourglassEmpty,
  IconLockFilled,
  IconMars,
  IconPalette,
  IconTransfer,
  IconVenus,
} from '@tabler/icons-react';
import { GENDER } from '@/shared/const/gender';
import { periodB } from '@/shared/libs/dates';
import { components } from '@/shared/types/api/TeraApi';
import ItemsTable from '@/app/items/components/Table';
import { useTranslation } from '@/i18n/client';

export function Item({ item }: { item: Promise<components['schemas']['ItemDTO'] | undefined> }) {
  const { t } = useTranslation('item');
  const itemData = use(item);

  const classes =
    itemData?.classes && itemData.classes.length > 0 ? itemData.classes.split(',') : [];
  const races = itemData?.races && itemData.races.length > 0 ? itemData.races.split(',') : [];
  return (
    <Card withBorder p={0}>
      <Grid p={10}>
        <Grid.Col span={{ base: 2, md: 1 }}>
          <div style={{ position: 'relative', left: 0, top: 0 }}>
            <Image
              className={'icon_grade_' + itemData?.grade}
              src={itemData?.icon || ''}
              alt='icon'
              width={64}
              height={64}
              style={{ position: 'relative', left: 0, top: 0 }}
            />
            <Image
              src={'/static/images/grade/icon_grade_' + itemData?.grade + '.png'}
              alt='grade'
              width={16}
              height={16}
              style={{ position: 'absolute', top: 0, left: 0 }}
            />
          </div>
        </Grid.Col>
        <Grid.Col span={{ base: 10, md: 5 }}>
          <h3 className={'item_grade_' + itemData?.grade} style={{ margin: 0 }}>
            RU: {itemData && itemData?.name['ru'].length > 0 ? itemData?.name['ru'] : 'NO NAME'}
            <br />
            EN: {itemData && itemData?.name['en'].length > 0 ? itemData?.name['en'] : 'NO NAME'}
          </h3>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }} p={0}>
          <Table>
            <Table.Tbody>
              <Table.Tr style={{ paddingTop: '5px' }}>
                <Table.Td
                  style={{
                    fontSize: '22px',
                    fontWeight: 500,
                    width: '120px',
                  }}
                >
                  {t('restriction')}
                </Table.Td>
                <Table.Td
                  style={{
                    height: '50px',
                    alignItems: 'center',
                    verticalAlign: 'center',
                    display: 'flex',
                  }}
                >
                  {itemData && itemData.level > 1 && (
                    <HoverCard shadow='md'>
                      <HoverCard.Target>
                        <span
                          style={{
                            fontWeight: 'bold',
                            fontSize: '20px',
                            color: 'cyan',
                          }}
                        >
                          {itemData.level}
                        </span>
                      </HoverCard.Target>
                      <HoverCard.Dropdown>
                        <Text size='sm'>{t('tooltips.level', { level: itemData.level })}</Text>
                      </HoverCard.Dropdown>
                    </HoverCard>
                  )}
                  {itemData && !itemData.obtainable && (
                    <HoverCard shadow='md'>
                      <HoverCard.Target>
                        <IconLockFilled
                          size={20}
                          style={{
                            margin: 0,
                            marginLeft: '10px',
                            color: 'pink',
                          }}
                        />
                      </HoverCard.Target>
                      <HoverCard.Dropdown>
                        <Text size='sm'>{t('tooltips.obtainable')}</Text>
                      </HoverCard.Dropdown>
                    </HoverCard>
                  )}
                  {itemData && itemData.tradable ? (
                    <HoverCard shadow='md'>
                      <HoverCard.Target>
                        <IconTransfer
                          size={20}
                          style={{
                            margin: 0,
                            marginLeft: '10px',
                            color: 'green',
                          }}
                        />
                      </HoverCard.Target>
                      <HoverCard.Dropdown>
                        <Text size='sm'>{t('tooltips.tradable.yes')}</Text>
                      </HoverCard.Dropdown>
                    </HoverCard>
                  ) : (
                    <HoverCard shadow='md'>
                      <HoverCard.Target>
                        <IconTransfer
                          size={20}
                          style={{
                            margin: 0,
                            marginLeft: '10px',
                            color: 'pink',
                          }}
                        />
                      </HoverCard.Target>
                      <HoverCard.Dropdown>
                        <Text size='sm'>{t('tooltips.tradable.no')}</Text>
                      </HoverCard.Dropdown>
                    </HoverCard>
                  )}
                  {itemData && itemData.dyeable && (
                    <HoverCard shadow='md'>
                      <HoverCard.Target>
                        <IconPalette
                          size={20}
                          style={{
                            margin: 0,
                            marginLeft: '10px',
                            color: 'orange',
                          }}
                        />
                      </HoverCard.Target>
                      <HoverCard.Dropdown>
                        <Text size='sm'>{t('tooltips.dyeable')}</Text>
                      </HoverCard.Dropdown>
                    </HoverCard>
                  )}
                  {itemData && itemData.gender === GENDER.MALE && (
                    <HoverCard shadow='md'>
                      <HoverCard.Target>
                        <IconMars
                          size={20}
                          style={{
                            margin: 0,
                            marginLeft: '10px',
                            color: '#0000ff',
                          }}
                        />
                      </HoverCard.Target>
                      <HoverCard.Dropdown>
                        <Text size='sm'>{t('tooltips.gender.male')}</Text>
                      </HoverCard.Dropdown>
                    </HoverCard>
                  )}
                  {itemData && itemData.gender === GENDER.FEMALE && (
                    <HoverCard shadow='md'>
                      <HoverCard.Target>
                        <IconVenus
                          size={20}
                          style={{
                            margin: 0,
                            marginLeft: '10px',
                            color: '#d71868',
                          }}
                        />
                      </HoverCard.Target>
                      <HoverCard.Dropdown>
                        <Text size='sm'>{t('tooltips.gender.female')}</Text>
                      </HoverCard.Dropdown>
                    </HoverCard>
                  )}
                  {itemData?.periodByWebAdmin ? (
                    <HoverCard shadow='md'>
                      <HoverCard.Target>
                        <IconHourglassEmpty
                          size={20}
                          style={{
                            margin: 0,
                            marginLeft: '10px',
                            color: 'red',
                          }}
                        />
                      </HoverCard.Target>
                      <HoverCard.Dropdown>
                        <Text size='sm'>{t('tooltips.periodByWebAdmin')}</Text>
                      </HoverCard.Dropdown>
                    </HoverCard>
                  ) : itemData?.period ? (
                    <HoverCard shadow='md'>
                      <HoverCard.Target>
                        <IconHourglassEmpty
                          size={20}
                          style={{
                            margin: 0,
                            marginLeft: '10px',
                            color: 'orange',
                          }}
                        />
                      </HoverCard.Target>
                      <HoverCard.Dropdown>
                        <Text size='sm'>
                          {t('tooltips.period', { period: periodB(itemData.period, t) })}
                        </Text>
                      </HoverCard.Dropdown>
                    </HoverCard>
                  ) : null}
                </Table.Td>
              </Table.Tr>
              {classes.length > 0 && (
                <Table.Tr>
                  <Table.Td style={{ fontSize: '22px', fontWeight: 500 }}>
                    {t('per_class')}
                  </Table.Td>
                  <Table.Td>
                    <ClassList classes={classes} />
                  </Table.Td>
                </Table.Tr>
              )}
              {races.length > 0 && (
                <Table.Tr>
                  <Table.Td style={{ fontSize: '22px', fontWeight: 500 }}>{t('per_race')}</Table.Td>
                  <Table.Td>
                    <RaceList races={races} gender={itemData?.gender || 0} />
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Grid.Col>
      </Grid>
      {itemData && (
        <Tabs
          color='teal'
          defaultValue={
            itemData.contain && itemData.contain.length > 0
              ? 'contain'
              : itemData.inside && itemData.inside.length > 0
                ? 'inside'
                : itemData.similar && itemData.similar.length > 0
                  ? 'similar'
                  : 'none'
          }
        >
          <Tabs.List>
            {itemData.contain && itemData.contain.length > 0 && (
              <Tabs.Tab value='contain'>{t('tabs.contain')}</Tabs.Tab>
            )}
            {itemData.inside && itemData.inside.length > 0 && (
              <Tabs.Tab value='inside' color='blue'>
                {t('tabs.inside')}
              </Tabs.Tab>
            )}
            {itemData.similar && itemData.similar.length > 0 && (
              <Tabs.Tab value='similar' color='cyan'>
                {t('tabs.similar')}
              </Tabs.Tab>
            )}
          </Tabs.List>

          {itemData?.contain && itemData.contain.length > 0 && (
            <Tabs.Panel value='contain'>
              <ItemsTable items={itemData.contain} />
            </Tabs.Panel>
          )}
          {itemData?.inside && itemData.inside.length > 0 && (
            <Tabs.Panel value='inside'>
              <ItemsTable items={itemData.inside} />
            </Tabs.Panel>
          )}
          {itemData?.similar && itemData.similar.length > 0 && (
            <Tabs.Panel value='similar'>
              <ItemsTable items={itemData.similar} />
            </Tabs.Panel>
          )}
        </Tabs>
      )}
    </Card>
  );
}

export function ClassList({ classes }: { classes: string[] }) {
  const { t } = useTranslation('common');
  return (
    <span style={{ width: '100%' }}>
      {classes.map((i) => (
        <HoverCard shadow='md' key={i}>
          <HoverCard.Target>
            <Image
              src={'/static/images/classes/' + i + '.svg'}
              alt='class icon'
              width={28}
              height={28}
              style={{ display: 'inline-block', paddingTop: '3px' }}
            />
          </HoverCard.Target>
          <HoverCard.Dropdown>
            <Text size='sm'>{t('classes.' + i)}</Text>
          </HoverCard.Dropdown>
        </HoverCard>
      ))}
    </span>
  );
}

export function RaceList({ races, gender }: { races: string[]; gender: GENDER }) {
  const { t } = useTranslation('common');
  return (
    <span style={{ width: '100%' }}>
      {races.map((i) => (
        <HoverCard shadow='md' key={i}>
          <HoverCard.Target>
            <Image
              src={
                '/static/images/races/' + (i === '4' && gender === GENDER.FEMALE ? 6 : i) + '.svg'
              }
              alt='race icon'
              width={28}
              height={28}
              style={{ display: 'inline-block', paddingTop: '3px' }}
            />
          </HoverCard.Target>
          <HoverCard.Dropdown>
            <Text size='sm'>{t('races.' + (i === '4' && gender === GENDER.FEMALE ? 6 : i))}</Text>
          </HoverCard.Dropdown>
        </HoverCard>
      ))}
    </span>
  );
}

export default Item;
